import React, { useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  LinearProgress,
  Chip,
  IconButton,
  Badge,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { EndCondition, EndConditionResult } from '../types';

interface EndConditionStatusProps {
  endConditions: EndCondition[];
  conditionResults: EndConditionResult[];
  isLoading?: boolean;
  onRefresh?: () => void;
}

// Helper function to format reasoning into bullet points
const formatReasoning = (reasoning: string): string[] => {
  // Check if the reasoning already has numbered points (e.g., "1. Point one 2. Point two")
  if (/\d+\.\s/.test(reasoning)) {
    // Split by number followed by period and space
    return reasoning.split(/(?=\d+\.\s)/).filter(point => point.trim().length > 0);
  }
  
  // Check if the reasoning has bullet points
  if (/•\s/.test(reasoning) || /\*\s/.test(reasoning)) {
    // Split by bullet points
    return reasoning.split(/(?=[•\*]\s)/).filter(point => point.trim().length > 0);
  }
  
  // If no structured format is detected, treat as a single point
  return [reasoning];
};

const EndConditionStatus: React.FC<EndConditionStatusProps> = ({
  endConditions,
  conditionResults,
  isLoading = false,
  onRefresh,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  // Count how many conditions are met
  const metCount = conditionResults.filter(result => result.isMet).length;
  const totalCount = endConditions.length;
  const progress = totalCount > 0 ? (metCount / totalCount) * 100 : 0;

  // Get condition details by ID
  const getConditionById = (id: string) => {
    return endConditions.find(condition => condition.id === id);
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // If there are no end conditions, don't render anything
  if (totalCount === 0) {
    return null;
  }

  return (
    <>
      {/* Status indicator - Simplified for top bar */}
      <Box 
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          flexGrow: 1,
        }}
        onClick={handleOpenDialog}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <Typography variant="body2" sx={{ mr: 2, fontWeight: 'medium' }}>
              {metCount}/{totalCount} met
            </Typography>
            <Badge 
              badgeContent={metCount} 
              color="success" 
              sx={{ mr: 1 }}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <InfoIcon fontSize="small" color="action" />
              </Box>
            </Badge>
          </Box>
          <Box sx={{ flexGrow: 1, maxWidth: 300 }}>
            <LinearProgress
              variant={isLoading ? "indeterminate" : "determinate"}
              value={progress}
              color={metCount === totalCount ? "success" : "primary"}
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>
        </Box>
      </Box>

      {/* Detailed dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          End Condition Status
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {metCount === totalCount
                ? "All end conditions have been met!"
                : `${metCount} out of ${totalCount} end conditions have been met.`}
            </Typography>
            <LinearProgress
              variant={isLoading ? "indeterminate" : "determinate"}
              value={progress}
              sx={{ height: 10, borderRadius: 1, mb: 3 }}
              color={metCount === totalCount ? "success" : "primary"}
            />
          </Box>

          {conditionResults.map((result) => {
            const condition = getConditionById(result.conditionId);
            if (!condition) return null;

            return (
              <Paper
                key={result.conditionId}
                elevation={1}
                sx={{
                  p: 2,
                  mb: 2,
                  borderLeft: 6,
                  borderColor: result.isMet ? 'success.main' : 'error.main',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {result.isMet ? (
                    <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  ) : (
                    <CancelIcon color="error" sx={{ mr: 1 }} />
                  )}
                  <Typography variant="subtitle1">
                    {condition.type.charAt(0).toUpperCase() + condition.type.slice(1)}
                  </Typography>
                  <Chip
                    label={result.isMet ? 'Met' : 'Not Met'}
                    color={result.isMet ? 'success' : 'error'}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Box>

                <Typography variant="body2" gutterBottom>
                  {condition.description}
                </Typography>

                {condition.type === 'keyword' && condition.parameters && condition.parameters.keyword && (
                  <Typography variant="body2" color="text.secondary">
                    Keyword: {condition.parameters.keyword}
                  </Typography>
                )}

                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                    Confidence:
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(result.confidence * 100, 100)} // Ensure value doesn't exceed 100
                    sx={{ flexGrow: 1, height: 6, borderRadius: 1 }}
                    color={result.confidence > 0.7 ? 'success' : result.confidence > 0.4 ? 'warning' : 'error'}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1, minWidth: 40 }}>
                    {Math.min(Math.round(result.confidence * 100), 100)}%
                  </Typography>
                </Box>

                {result.reasoning && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Reasoning:
                    </Typography>
                    {formatReasoning(result.reasoning).map((point, index) => (
                      <Box key={index} sx={{ display: 'flex', ml: 2, mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>•</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {point}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            );
          })}
        </DialogContent>
        <DialogActions>
          {onRefresh && (
            <Button onClick={() => {
              onRefresh();
              handleCloseDialog();
            }}>
              Refresh
            </Button>
          )}
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EndConditionStatus;
